import settings, { SUBMIT_PRESCREENING_POST_URL } from '@/constants/constants';
import { FF_ENABLE_INC_VIDEO_CALL } from '@/constants/featureFlags';
import { getPreScreeningShortSteps, SINGLE_FIELD_TRACKING_MAP } from '@/data/PreScreening/pre-screening-short';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import useGoogleTagManager from '@/hooks/useGoogleTagManager';
import { formatStringToSha256Hash } from '@/utils/ga-hashing';
import { getBrowserTimezone } from '@/utils/timezone';
import { Box, CircularProgress } from '@montugroup/design-system';
import type { Step } from '@montugroup/form-builder';
import { FormBuilder } from '@montugroup/form-builder';
import { useTheme } from '@mui/material';
import { useState } from 'react';
import type { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

export const PreScreeningFormShort = () => {
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const { study } = Object.fromEntries(queryParams);
  const { sendGoogleAnalyticsEvent } = useGoogleTagManager();
  const { flags, loading } = useFeatureFlags();
  const ffEnableINCVideoCall = flags[FF_ENABLE_INC_VIDEO_CALL];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const theme = useTheme();

  const POST_URL = `${settings.xApiUrl}${SUBMIT_PRESCREENING_POST_URL}`;

  const FORM_ID = 'pre-screening-short';
  const formSteps = getPreScreeningShortSteps(theme, ffEnableINCVideoCall);

  const handleTransform = (formValues: FieldValues) => {
    const {
      emailStep: { email, subscribeProductServices: isSubscribedToMarketing },
    } = formValues;

    const landingPage = study ? `form/${String(study)}` : 'form';

    return {
      data: {
        medicalCondition: 'Yes',
        triedTreatment: 'Yes',
        treatment: 'Yes',
        pregnant: 'No',
        psychosis: 'No',
        emailAndPrivacy: {
          email,
          acceptPrivacyNotice: true,
          subscribeProductServices: isSubscribedToMarketing,
        },
        variant: formValues.variant,
        study: study ? String(study) : undefined,
        landingPage,
        timezone: getBrowserTimezone(),
      },
    };
  };

  const handleAfterSubmitSuccess = (formValues: FieldValues, responseBody?: object) => {
    setIsSubmitting(true);
    const patientEmail = formValues.data.emailAndPrivacy.email;
    // The API decides which consult flow the patient belongs in.
    const { consultFlow } = (responseBody ?? {}) as { consultFlow?: 'd2d' | 'inc' };
    // Carried through rather than rebuilt, so `promocode` reaches the nurse and
    // doctor forms that read it.
    const params = new URLSearchParams(window.location.search);
    params.set('email', patientEmail);
    // The nurse flow is where consult-eligible sends a patient with no flow
    // anyway, and setting it unconditionally means a stale one on the URL
    // cannot survive.
    params.set('consultFlow', consultFlow ?? 'inc');
    const consultEligibleSearch = params.toString();
    const shaUserEmail = patientEmail ? formatStringToSha256Hash(patientEmail) : null;

    const payload = {
      clickId: 'email-privacy',
      event: 'prescreening_complete',
      result: 'Eligible',
      sha_email: shaUserEmail,
      step_name: 'End of Screening',
      step_number: '8',
    };

    // Form complete tracking event
    sendGoogleAnalyticsEvent('prescreening_complete', payload);

    setTimeout(() => {
      if (window.parent !== window) {
        /*
          The expectation is that this form will be contained in an iframe
          within a modal on the http://alternaleaf.com.au website. So in order
          to do a redirect, after successful form submission, we call the parent window.
        */
        window.parent.postMessage(
          {
            type: 'PRESCREENING_SHORT_FORM_SUBMITTED',
            redirectUrl: `${settings.preScreeningUrl}/consult-eligible?${consultEligibleSearch}`,
          },
          settings.alternaLeafUrl,
        );
      } else {
        navigate(`/consult-eligible?${consultEligibleSearch}`);
      }
    }, 500);
  };

  const logEvents = (event: string, formValues: FieldValues) => {
    const stepIndex = formValues.current;
    let payload;

    // Single-field tracking events
    const fieldTrackingValues = SINGLE_FIELD_TRACKING_MAP[formValues.name];

    if (fieldTrackingValues) {
      payload = {
        ...fieldTrackingValues,
        event: 'prescreening_progress',
        value: formValues.value,
      };
    }

    if (payload) {
      sendGoogleAnalyticsEvent(event, payload);
    }

    // Step tracking event
    if (stepIndex === 1) {
      sendGoogleAnalyticsEvent(event, {
        clickId: 'sps-eligible',
        event: 'prescreening_progress',
        step_name: 'SPS Eligible',
      });
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={80} />
      </Box>
    );
  }
  return (
    <Box>
      {isSubmitting ? (
        <Box sx={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={80} />
        </Box>
      ) : (
        <FormBuilder
          formId={FORM_ID}
          postUrl={POST_URL}
          steps={formSteps as Step[]}
          logEvents={logEvents}
          transform={handleTransform}
          afterSubmitSuccess={handleAfterSubmitSuccess}
          disableLoadingBackdrop
        />
      )}
    </Box>
  );
};
